<template>
  <popup
    :opened="shown"
    @close="close"
  >
    <!-- BEGIN SELECTION -->
    <div v-if="currentStage === 0">
      <div class="my-5 mx-20 lg:my-15 lg:mx-40 2xl:my-20 2xl:mx-32">
        <div class="flex flex-col justify-center items-center">
          <h3 class="mb-10">
            Sélectionnez une photo de profil
          </h3>

          <h3>OU</h3>

          <action-button
            bg-color="bg-pink-600"
            bg-color-hover="bg-white"
            bg-color-square="bg-white"
            bg-color-square-hover="bg-pink-600"
            border-color="border-pink-600"
            :disabled="false"
            icon="arrow-right_1"
            icon-color="text-white"
            libelle="Choisir une autre image"
            text-color="text-white"
            @clickAction="toStage(1)"
          />
        </div>
      </div>
    </div>
    <!-- END SELECTION -->

    <!-- BEGIN NEW_UPLOAD -->
    <div
      v-if="currentStage === 1"
      v-cloak
      @drop.prevent="tempUpload"
      @dragover.prevent
    >
      <div class="my-5 mx-20 lg:my-15 lg:mx-40 2xl:my-20 2xl:mx-32">
        <h3 class="mb-10">
          Importer une image
        </h3>
        <div
          class="bg-indigo-100 rounded-lg flex flex-col items-center space-y-3 py-24"
        >
          <icon
            name="file"
            class="text-pink-600 w-12 h-12"
            :class="{ 'animate-pulse': loading }"
          />
          <div
            v-if="!loading"
            class="flex flex-col items-center space-y-3"
          >
            <h3 class="text-indigo-600">
              Glissez & Déposez
            </h3>
            <input
              type="file"
              id="upload-file"
              class="hidden"
              accept="image/jpeg, image/png"
              @change="tempUpload"
            >
            <label
              for="upload-file"
              class="text-gray-600 cursor-pointer text-lg"
            >
              ou <span class="underline">importez</span> votre
              image
            </label>
            <div
              v-if="tooManyFiles"
              class="text-red-500 text-sm"
            >
              Veuillez ne sélectionner qu'une seule image à la
              fois
            </div>
            <div
              v-if="formatNotSupported"
              class="text-red-500 text-sm"
            >
              Format de l'image non supporté
            </div>
          </div>
          <div v-if="loading">
            <h6 class="text-gray-600 animate-pulse">
              Image en cours d'envoi...
            </h6>
          </div>
        </div>
      </div>
    </div>
    <!-- END NEW_UPLOAD -->

    <!-- BEGIN NEW_CONFIRM -->
    <div v-if="currentStage === 3">
      <div class="my-5 mx-20 lg:my-15 lg:mx-40 2xl:my-20 2xl:mx-32">
        <div class="flex flex-col justify-center items-center">
          <h5
            v-if="self"
            class="mb-5"
          >
            Votre nouvelle image de profil:
          </h5>
          <h5
            v-else-if="firstname"
            class="mb-5"
          >
            La nouvelle image de profil {{ firstname }}:
          </h5>
          <img
            :src="fileTemp.path"
            class="object-cover max-w-64 w-64 h-64 2xl:max-w-80 2xl:w-80 2xl:h-80 mb-5 rounded-full border border-indigo-300"
          >
          <action-button
            bg-color="bg-pink-600"
            bg-color-hover="bg-white"
            bg-color-square="bg-white"
            bg-color-square-hover="bg-pink-600"
            border-color="border-pink-600"
            :disabled="false"
            icon="arrow-right_1"
            icon-color="text-white"
            libelle="Continuer"
            text-color="text-white"
            @clickAction="confirm()"
          />
          <span
            class="text-2sm mt-10 text-gray-500 font-extra-light text-center italic"
          >            Il vous sera possible de repositionner l'image à
            l'intérieur du cadre dans une prochaine version. Merci
            de votre patience!
          </span>
        </div>
      </div>
    </div>
    <!-- END NEW_CONFIRM -->

    <!-- BEGIN SUCCESS -->
    <div v-if="currentStage === 4">
      <div class="my-5 mx-20 lg:my-15 lg:mx-40 2xl:my-20 2xl:mx-32">
        <div class="flex flex-col justify-center items-center">
          <h3
            v-if="self"
            class="text-indigo-600 mb-10"
          >
            Votre photo de profil a bien été mise à jour !
          </h3>
          <h3
            v-else
            class="text-indigo-600 mb-10"
          >
            La photo de profil {{ firstname }} a bien été mise à
            jour !
          </h3>

          <div class="w-full flex justify-center">
            <illustration
              name="basket"
              class="w-full"
            />
          </div>
        </div>
      </div>
    </div>
    <!-- END SUCCESS -->
  </popup>
</template>

<script>
import Popup from './Popup';
import Icon from './Icon';
import Illustration from './Illustration';
import ActionButton from './ActionButton';
import axios from '../api';
import { mapActions, mapGetters, mapMutations } from 'vuex';

//Etapes : selection image existante ╕─────────────────────────────────╒═══> crop ═══╕─────────────────────────────────────────────╒═══> succès
//                                   └───> ajouter nouveau (upload) ───┴─────────────┴───> ajouter nouveau (nom + confirmation) ───┘
const SELECTION = 0;
const NEW_UPLOAD = 1;
const CROPPING = 2;
const NEW_CONFIRM = 3;
const SUCCESS = 4;
const ERROR = 5;

export default {
  name: 'ProfilePictureModal',

  props: {
    shown: Boolean,
    targetObject: Object,
    specificMedia: {
      type: String,
      required: false,
      default: 'profile-picture',
    },
    typeProfilePicture: {
      type: String,
      required: false,
      default: 'users',
    },
  },

  data() {
    return {
      currentStage: 1,
      loading: false,
      tooManyFiles: false,
      formatNotSupported: false,
      file: '',
      fileTemp: {},
      formData: null,
      fileId: null,

      errors: [],
    };
  },

  components: {
    Popup,
    Icon,
    Illustration,
    ActionButton,
  },

  computed: {
    ...mapGetters({
      loggedUser: 'auth/user',
      backdoor: 'app/backdoor',
    }),

    self() {
      return (
        this.typeProfilePicture === 'users' &&
                this.loggedUser.id === this.targetObject.id
      );
    },

    firstname() {
      if (this.typeProfilePicture != 'users') {
        return null;
      }

      const vowels = ['a', 'e', 'i', 'o', 'u'];
      if (
        vowels.includes(
          this.targetObject.firstname.toLowerCase().charAt(0)
        )
      ) {
        return 'd\'' + this.targetObject.firstname;
      }
      return 'de ' + this.targetObject.firstname;
    },
  },

  methods: {
    ...mapMutations({
      SET_BACKDOOR: 'app/SET_BACKDOOR',
    }),

    ...mapActions({
      me: 'auth/me',
    }),

    toStage(stage) {
      this.currentStage = stage;
    },

    close() {
      this.$emit('close');
    },

    // ================ File handling ================
    async tempUpload(event) {
      this.loading = true;
      let files = event.target.files || event.dataTransfer.files;

      if (files.length > 1) {
        this.tooManyFiles = true;
        return;
      }

      if (
        files[0].type !== 'image/jpeg' &&
                files[0].type !== 'image/png'
      ) {
        this.formatNotSupported = true;
        return;
      }

      this.file = files[0];

      //Creating the request object
      let formData = new FormData();

      formData.append('image', this.file, this.file.name);

      await axios
        .post(
          '/api/' +
                        this.typeProfilePicture +
                        '/' +
                        this.targetObject.id +
                        '/' +
                        this.specificMedia,
          formData
        )
        .then((response) => {
          this.fileId = response.data.id;
          this.fileTemp.path = response.data.path;
          this.toStage(3);
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    async confirm() {
      await axios
        .put(
          '/api/' +
                        this.typeProfilePicture +
                        '/' +
                        this.targetObject.id +
                        '/' +
                        this.specificMedia +
                        '/' +
                        this.fileId
        )
        .then((response) => {
          if (response.status === 200) {
            this.toStage(4);
            this.SET_BACKDOOR(this.backdoor + 1);
            if (this.self) {
              this.loggedUser.profile_image = response.data;
            }
            if (!this.self) {
              this.$emit(
                'changeProfileImageToAnotherUser',
                response.data
              );
            }
          }
        })
        .catch((err) => {
          console.log(err);
        });
    },
  },
};
</script>

<style scoped></style>
